<script setup lang="ts">
import { computed, ref } from 'vue';
import { deleteContractManagementDate, type ContractManagementDateInDb } from '@/api';
import { DATE_TYPE_LABELS, toDateRow } from '@/services/contractManagement/dateRow';
import { staticEntityName } from '@/utils/contractManagementView/staticEntityName';
import { logger } from '@/utils/logger';
import EntityFormDialog from '@/views/contractManagement/EntityFormDialog.vue';
import { fileDateConfig } from '@/views/contractManagement/entityListConfig';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaPopconfirm from '@/components/design/composite/GaiaPopconfirm.vue';
import GaiaSubPanel from '@/components/design/composite/GaiaSubPanel.vue';
import { IconAdd, IconInfo } from '@/components/design/icons';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * The contract file's term — when it takes effect and when it runs out — read
 * as facts about the file beside its description rather than as an entity tab.
 * Two stacked panels, start above end, that share the height they are given,
 * so they line up with the description beside them. Each date reads
 * "Label: date"; its description opens from the info icon. Dates are
 * extracted, or added by hand from a panel's plus — a type may hold several.
 * Both known types always show, so an empty one is still where to add it.
 */
const props = defineProps<{
  dates: ContractManagementDateInDb[];
  loading: boolean;
  contractFileUid: string;
  /** The revision new dates go on; null for the file's current one. */
  revisionUid: string | null;
}>();

const emit = defineEmits<{ refresh: [] }>();

const message = useGaiaToast();

const showForm = ref(false);
const editing = ref<ContractManagementDateInDb | null>(null);
/** The type a new date is created as — fixed by the panel it was added from. */
const createType = ref('');
const formConfig = computed(() => fileDateConfig(props.contractFileUid, props.revisionUid));
/** The date whose delete is in flight — its trash is disabled until it settles. */
const deletingUid = ref<string | null>(null);

/** Known types in reading order — the term starts before it ends. */
const TYPE_ORDER: string[] = Object.keys(DATE_TYPE_LABELS);

/**
 * One panel per known type, empty or not, then any other type the file has. A
 * type with several dates lists them all under one label; an unknown type
 * shows, after the known ones, under its raw name.
 */
const groups = computed(() => {
  const byType = new Map<string, ContractManagementDateInDb[]>(TYPE_ORDER.map(type => [type, []]));
  for (const date of props.dates) {
    byType.set(date.type, [...(byType.get(date.type) ?? []), date]);
  }
  const rank = (type: string): number => {
    const index = TYPE_ORDER.indexOf(type);
    return index === -1 ? TYPE_ORDER.length : index;
  };
  return [...byType.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([type, dates]) => ({
      type,
      label: DATE_TYPE_LABELS[type] ?? type,
      dates,
      // Only the known types can be created; an unknown one is read-only here.
      creatable: TYPE_ORDER.includes(type),
    }));
});

function openForm(date: ContractManagementDateInDb): void {
  editing.value = date;
  showForm.value = true;
}

function openCreate(type: string): void {
  editing.value = null;
  createType.value = type;
  showForm.value = true;
}

async function handleDelete(uid: string): Promise<void> {
  deletingUid.value = uid;
  try {
    await deleteContractManagementDate({ path: { uid }, throwOnError: true });
    message.success('Date deleted');
    emit('refresh');
  } catch (error) {
    logger.error('Error deleting date:', error);
    message.error(error instanceof Error ? error.message : 'Failed to delete date');
  } finally {
    deletingUid.value = null;
  }
}
</script>

<template>
  <n-flex vertical :size="12" :wrap="false" :style="{ height: '100%' }">
    <gaia-sub-panel v-if="loading" :style="{ flex: 1 }">
      <gaia-text size="control" tone="faint">Loading…</gaia-text>
    </gaia-sub-panel>
    <gaia-sub-panel v-for="group in groups" v-else :key="group.type" :style="{ flex: 1 }">
      <n-flex align="flex-start" :size="6" :wrap="false">
        <!-- Line-height matches a date row, so the label sits on the first one. -->
        <gaia-text size="control" tone="subtle" :style="{ flex: 'none', lineHeight: '20px' }">
          {{ group.label }}:
        </gaia-text>
        <n-flex vertical :size="4" :wrap="false" :style="{ flex: 1, minWidth: 0 }">
          <!-- Line-height matches a date row, like the label beside it. -->
          <gaia-link
            v-if="!group.dates.length"
            :underline="false"
            :style="{ lineHeight: '20px' }"
            @click="openCreate(group.type)"
          >
            Add date
          </gaia-link>
          <n-flex
            v-for="date in group.dates"
            :key="date.uid"
            align="center"
            :size="6"
            :wrap="false"
          >
            <gaia-link :underline="false" @click="openForm(date)">
              <!-- Extracted without a date (its description says why): the link
                   is still the way to set one by hand. -->
              {{ date.date ? staticEntityName(date) : 'Set date' }}
            </gaia-link>
            <gaia-popover v-if="date.description" :raw="false" placement="bottom-start">
              <template #trigger>
                <gaia-icon-button :dimension="20" quaternary title="Description">
                  <n-icon :component="IconInfo" :size="13" />
                </gaia-icon-button>
              </template>
              <!-- A block of its own: the text is inline, so its width can't cap the panel. -->
              <n-flex vertical :wrap="false" :style="{ maxWidth: '300px' }">
                <gaia-text size="control" tone="text">{{ date.description }}</gaia-text>
              </n-flex>
            </gaia-popover>
            <gaia-popconfirm title="Delete this date?" @confirm="handleDelete(date.uid)">
              <template #trigger>
                <gaia-delete-button
                  size="xs"
                  subject="date"
                  :disabled="deletingUid === date.uid"
                  :style="{ marginLeft: 'auto' }"
                />
              </template>
            </gaia-popconfirm>
          </n-flex>
        </n-flex>
        <gaia-icon-button
          v-if="group.dates.length && group.creatable"
          :dimension="20"
          quaternary
          title="Add date"
          @click="openCreate(group.type)"
        >
          <n-icon :component="IconAdd" :size="14" />
        </gaia-icon-button>
      </n-flex>
    </gaia-sub-panel>
  </n-flex>

  <entity-form-dialog
    v-model:show="showForm"
    :config="formConfig"
    :row="editing ? toDateRow(editing) : null"
    :row-name="editing ? staticEntityName(editing) : ''"
    :defaults="{ type: createType }"
    :locked-keys="['type']"
    @saved="emit('refresh')"
  />
</template>
